import React, { useEffect, useRef, useState, useCallback } from "react";

import "./confetti-overlay.scss";

const FRAME_COUNT = 36;
const FRAME_PATH = (index) =>
  `/images/confetti/Confetti-Completion_${index
    .toString()
    .padStart(5, "0")}.png`;

export const ConfettiOverlay = ({ loopCount = 1, shouldRun }) => {
  const [frameIndex, setFrameIndex] = useState(shouldRun ? 0 : FRAME_COUNT - 1);
  const [loopsCompleted, setLoopsCompleted] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [loadingError, setLoadingError] = useState(false);

  const requestRef = useRef();
  const lastFrameTimeRef = useRef(0);
  const preloadedImagesRef = useRef(new Map());
  const animationStartTimeoutRef = useRef();

  const frameDuration = 1000 / 24; // ~24fps

  // Preload all confetti images
  const preloadImages = useCallback(() => {
    let loadedCount = 0;
    let hasError = false;

    const onImageLoad = () => {
      loadedCount++;
      if (loadedCount === FRAME_COUNT && !hasError) {
        setImagesLoaded(true);
        setLoadingError(false);
      }
    };

    const onImageError = (index) => {
      console.warn(`Failed to load confetti frame ${index}`);
      hasError = true;
      setLoadingError(true);
    };

    // Preload all images
    for (let i = 0; i < FRAME_COUNT; i++) {
      if (!preloadedImagesRef.current.has(i)) {
        const img = new Image();
        img.onload = onImageLoad;
        img.onerror = () => onImageError(i);
        img.src = FRAME_PATH(i);
        preloadedImagesRef.current.set(i, img);
      } else {
        // Image already loaded
        loadedCount++;
      }
    }

    // If all images are already cached, set loaded immediately
    if (loadedCount === FRAME_COUNT && !hasError) {
      setImagesLoaded(true);
    }
  }, []);

  const animate = useCallback(
    (time) => {
      if (!lastFrameTimeRef.current) lastFrameTimeRef.current = time;
      const elapsed = time - lastFrameTimeRef.current;

      if (elapsed >= frameDuration) {
        setFrameIndex((prev) => {
          const next = prev + 1;

          if (next >= FRAME_COUNT) {
            const updatedLoops = loopsCompleted + 1;

            if (updatedLoops >= loopCount) {
              cancelAnimationFrame(requestRef.current);
              return FRAME_COUNT - 1; // Stop on last frame
            } else {
              setLoopsCompleted(updatedLoops);
              lastFrameTimeRef.current = time;
              return 0;
            }
          }

          lastFrameTimeRef.current = time;
          return next;
        });
      }

      requestRef.current = requestAnimationFrame(animate);
    },
    [loopsCompleted, loopCount, frameDuration]
  );

  const startAnimation = useCallback(() => {
    if (imagesLoaded && !loadingError) {
      setFrameIndex(0);
      setLoopsCompleted(0);
      lastFrameTimeRef.current = 0;
      requestRef.current = requestAnimationFrame(animate);
    }
  }, [imagesLoaded, loadingError, animate]);

  // Preload images on mount
  useEffect(() => {
    preloadImages();

    return () => {
      // Cleanup timeout on unmount
      if (animationStartTimeoutRef.current) {
        clearTimeout(animationStartTimeoutRef.current);
      }
    };
  }, [preloadImages]);

  useEffect(() => {
    // Clear any existing timeout
    if (animationStartTimeoutRef.current) {
      clearTimeout(animationStartTimeoutRef.current);
    }

    if (shouldRun) {
      if (imagesLoaded && !loadingError) {
        // Small delay to ensure DOM is ready
        animationStartTimeoutRef.current = setTimeout(startAnimation, 100);
      }
      // If images aren't loaded yet, the animation will start when they are loaded
    } else {
      setFrameIndex(FRAME_COUNT - 1); // Show last frame statically
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
    }

    return () => {
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
      if (animationStartTimeoutRef.current) {
        clearTimeout(animationStartTimeoutRef.current);
      }
    };
  }, [shouldRun, imagesLoaded, loadingError, startAnimation]);

  // Start animation when images finish loading and shouldRun is true
  useEffect(() => {
    if (shouldRun && imagesLoaded && !loadingError) {
      animationStartTimeoutRef.current = setTimeout(startAnimation, 100);
    }
  }, [shouldRun, imagesLoaded, loadingError, startAnimation]);

  // Fallback: if there's an error loading images, show the last frame
  const displayFrameIndex = loadingError ? FRAME_COUNT - 1 : frameIndex;

  return (
    <img
      src={FRAME_PATH(displayFrameIndex)}
      alt="confetti-frame"
      className="confetti-frame"
      style={{
        // Add a small fade-in effect when animation starts
        opacity: shouldRun && imagesLoaded ? 1 : 0.8,
        transition: "opacity 0.2s ease-in-out",
      }}
    />
  );
};

export default ConfettiOverlay;
